import Papa from 'papaparse';

// ---------------------------------------------------------------------------
// CSV roster import — small pure-function library used by the roster
// import wizard (src/pages/students-import).
//
// Design goals:
//
//   * Feed an Excel export back in without fixing headers by hand. Every
//     SIS in existence (PowerSchool, Skyward, Infinite Campus, Aeries,
//     EdPlan, Focus, etc.) exports CSV, but every one of them names the
//     columns slightly differently. HEADER_SYNONYMS below auto-maps a
//     bunch of common variants to our canonical field keys so the
//     teacher hits "Next" instead of a mapping puzzle.
//
//   * Never lose the teacher's data if a row is malformed. validate()
//     tags each row with { valid, warnings, errors } so the UI can show
//     "12 rows will import, 2 rows have missing names — fix them below
//     or skip them." Skipping a bad row never blocks the good ones.
//
//   * Detect probable duplicates against the existing roster. Match on
//     lower-cased firstName + lastName + dateOfBirth (when present).
//     Falling back to name-only when DOB isn't in the CSV means a
//     teacher who imports the same file twice doesn't end up with two
//     of every student.
//
// This module is pure JS with no React or Supabase deps so it's trivial
// to unit-test and reuse anywhere (e.g. a future "Import from EdPlan
// PDF" path could hand this the same row shape).
// ---------------------------------------------------------------------------

// The canonical field keys we map every CSV column onto. Order matters
// only for the mapping-dropdown UI, which shows them in this order.
export const CANONICAL_FIELDS = [
  {
    key: 'firstName',
    label: 'First name',
    required: true,
    hint: 'The student\'s given name.',
  },
  {
    key: 'lastName',
    label: 'Last name',
    required: true,
    hint: 'The student\'s family name.',
  },
  {
    key: 'dateOfBirth',
    label: 'Date of birth',
    required: false,
    hint: 'Any format Excel exports — MM/DD/YYYY, YYYY-MM-DD, etc.',
  },
  {
    key: 'grade',
    label: 'Grade',
    required: false,
    hint: 'K, 1–12, or Pre-K.',
  },
  {
    key: 'primaryDisability',
    label: 'Primary disability',
    required: false,
    hint: 'e.g. Autism, SLD, ED, OHI. We map common abbreviations.',
  },
  {
    key: 'iepStartDate',
    label: 'IEP start date',
    required: false,
    hint: 'The active IEP\'s effective date.',
  },
  {
    key: 'iepAnnualReviewDue',
    label: 'IEP annual review due',
    required: false,
    hint: 'When the annual review is due.',
  },
  {
    key: 'notes',
    label: 'Notes',
    required: false,
    hint: 'Free-text notes about the student.',
  },
  // A special sentinel — mapping a CSV column to "ignore" drops it on
  // the floor. We never surface external SIS ids on our side today.
  {
    key: '__ignore__',
    label: 'Ignore this column',
    required: false,
    hint: 'Skip this column when importing.',
  },
];

// Synonyms grouped by canonical key. Match is case-insensitive and
// ignores punctuation and whitespace, so "First-Name", "first name",
// "FIRST_NAME" and "firstname" all collapse to the same normalized
// token. See normalizeHeader() below.
const HEADER_SYNONYMS = {
  firstName: [
    'firstname', 'first', 'givenname', 'given', 'fname', 'firstnames',
    'studentfirstname', 'studentfirst',
  ],
  lastName: [
    'lastname', 'last', 'familyname', 'surname', 'lname',
    'studentlastname', 'studentlast',
  ],
  dateOfBirth: [
    'dob', 'dateofbirth', 'birthdate', 'birthday', 'birth',
    'studentbirthdate', 'studentdob',
  ],
  grade: [
    'grade', 'gradelevel', 'grd', 'gradenumber', 'currentgrade',
    'schoolgrade',
  ],
  primaryDisability: [
    'primarydisability', 'disability', 'disabilitycategory', 'category',
    'ideacategory', 'eligibility', 'primaryeligibility', 'diagnosis',
    'primarydiagnosis',
  ],
  iepStartDate: [
    'iepstartdate', 'iepstart', 'iepdate', 'iepeffectivedate',
    'iepbeginning', 'ieppresent',
  ],
  iepAnnualReviewDue: [
    'iepannualreviewdue', 'iepannualreview', 'annualreview',
    'annualreviewdue', 'iepreviewdate', 'nextreview', 'iepdue',
    'reviewdue',
  ],
  notes: ['notes', 'comment', 'comments', 'remark', 'remarks', 'note'],
};

// Common IDEA disability abbreviations districts stamp on their
// export. Keep this list conservative — anything we don't recognize
// falls through as free text into the primaryDisability field, which
// the AddStudent form also accepts.
const DISABILITY_SYNONYMS = {
  autism: ['autism', 'asd', 'autismspectrumdisorder'],
  intellectual: ['intellectual', 'id', 'intellectualdisability', 'mr', 'mid'],
  learning: ['learning', 'sld', 'specificlearningdisability', 'ld'],
  emotional: ['emotional', 'ed', 'emotionalbehavioraldisorder', 'ebd'],
  speech: ['speech', 'sli', 'speechlanguageimpairment', 'language', 'sl'],
  hearing: ['hearing', 'hi', 'deaf', 'hearingimpairment'],
  visual: ['visual', 'vi', 'blind', 'visualimpairment'],
  physical: ['physical', 'orthopedic', 'oi', 'physicaldisability'],
  multiple: ['multiple', 'md', 'multipledisabilities'],
  other: ['other', 'ohi', 'otherhealthimpairment', 'health'],
};

// Squash "First-Name", "first name", "FIRST_NAME" → "firstname".
function normalizeHeader(raw) {
  if (raw == null) return '';
  return String(raw).toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Try to auto-map a CSV header string onto one of our canonical fields
// by comparing its normalized form against the synonym table.
// Returns null if no match — the UI shows "Ignore" as the default in
// that case, and the teacher can override to any field.
export function suggestFieldForHeader(rawHeader) {
  const norm = normalizeHeader(rawHeader);
  if (!norm) return null;
  for (const [key, aliases] of Object.entries(HEADER_SYNONYMS)) {
    if (aliases.includes(norm)) return key;
  }
  return null;
}

// Parse the raw file text into { headers, rows, errors }. Uses PapaParse
// with sensible defaults — `header: false` because we want the raw
// header row so we can suggest mappings, `skipEmptyLines: 'greedy'`
// because Excel loves trailing blank rows, `dynamicTyping: false` so
// dates and grades stay as strings and we don't accidentally coerce
// "07" into 7 (kindergarten becomes 7th grade — real bug in an
// earlier prototype).
export function parseCsvText(text) {
  if (!text || typeof text !== 'string') {
    return { headers: [], rows: [], errors: [{ message: 'File was empty.' }] };
  }

  // Strip a UTF-8 BOM if Excel added one — otherwise our first column
  // header comes through as "\uFEFFFirst Name" and never matches.
  const cleaned = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  const result = Papa.parse(cleaned, {
    header: false,
    skipEmptyLines: 'greedy',
    dynamicTyping: false,
  });

  const grid = Array.isArray(result?.data) ? result.data : [];
  if (grid.length === 0) {
    return {
      headers: [],
      rows: [],
      errors: [{ message: 'The file didn\'t contain any rows.' }],
    };
  }

  const headers = (grid[0] || []).map((h) => (h == null ? '' : String(h).trim()));
  const rows = grid.slice(1).map((row, idx) => {
    const obj = {};
    headers.forEach((h, colIdx) => {
      obj[h] = row[colIdx] == null ? '' : String(row[colIdx]).trim();
    });
    obj.__rowIndex = idx + 2; // +1 to skip header, +1 to be 1-based
    return obj;
  });

  return { headers, rows, errors: result?.errors || [] };
}

// Build the initial mapping suggestion: { csvHeader: canonicalKey }.
// UI can start from this and let the teacher override.
export function buildInitialMapping(headers) {
  const mapping = {};
  const used = new Set();
  for (const header of headers) {
    if (!header) continue;
    const suggested = suggestFieldForHeader(header);
    if (suggested && !used.has(suggested)) {
      mapping[header] = suggested;
      used.add(suggested);
    } else {
      mapping[header] = '__ignore__';
    }
  }
  return mapping;
}

// Try really hard to interpret a date string. Handles the four common
// spreadsheet formats — MM/DD/YYYY, M/D/YYYY, YYYY-MM-DD, D-Mmm-YYYY —
// plus anything JavaScript's Date can parse itself as a fallback.
// Returns YYYY-MM-DD (what Supabase's date columns want) or null.
export function coerceDateOfBirth(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  // ISO already? Match YYYY-MM-DD or YYYY/MM/DD.
  const isoMatch = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // US-style M/D/YYYY or MM/DD/YY.
  const usMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/);
  if (usMatch) {
    let [, m, d, y] = usMatch;
    if (y.length === 2) {
      // 00-30 -> 2000s, 31-99 -> 1900s (matches Excel's default).
      const yr = parseInt(y, 10);
      y = yr <= 30 ? String(2000 + yr) : String(1900 + yr);
    }
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // Last-resort: let Date parse. If it works, format to ISO.
  const d = new Date(trimmed);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return null;
}

// Normalize a grade string. Districts export "K", "KG", "Kindergarten",
// "0", "0.0", etc. We map to the same option values the AddStudentModal
// dropdown uses, so a subsequent Edit-Student render doesn't show
// "Not set" when the underlying value is actually kindergarten.
export function coerceGrade(raw) {
  if (raw == null) return '';
  const norm = String(raw).toLowerCase().trim().replace(/[^a-z0-9]/g, '');
  if (!norm) return '';
  if (norm === 'prek' || norm === 'pk' || norm === 'preschool') return 'pre-k';
  if (norm === 'k' || norm === 'kg' || norm === 'kindergarten' || norm === '0') {
    return 'k';
  }
  const asInt = parseInt(norm, 10);
  if (!Number.isNaN(asInt) && asInt >= 1 && asInt <= 12) return String(asInt);
  // Fall back to whatever they gave us — the AddStudentModal accepts
  // free-form text in the grade column too.
  return String(raw).trim();
}

// Normalize a primary-disability abbreviation onto the AddStudentModal's
// dropdown values. Unknown values are kept verbatim so nothing gets
// destroyed.
export function coerceDisability(raw) {
  if (raw == null) return '';
  const norm = String(raw).toLowerCase().trim().replace(/[^a-z0-9]/g, '');
  if (!norm) return '';
  for (const [canonical, aliases] of Object.entries(DISABILITY_SYNONYMS)) {
    if (aliases.includes(norm)) return canonical;
  }
  return String(raw).trim();
}

// Apply the header → canonical-field mapping to a single parsed row,
// returning the shape our addStudent() function accepts.
export function rowToStudentDraft(row, mapping) {
  const draft = {
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    grade: '',
    primaryDisability: '',
    iepStartDate: null,
    iepAnnualReviewDue: null,
    notes: '',
  };
  for (const [csvHeader, canonicalKey] of Object.entries(mapping)) {
    if (!canonicalKey || canonicalKey === '__ignore__') continue;
    const value = row[csvHeader];
    if (value == null || value === '') continue;
    switch (canonicalKey) {
      case 'firstName':
        draft.firstName = String(value).trim();
        break;
      case 'lastName':
        draft.lastName = String(value).trim();
        break;
      case 'dateOfBirth':
        draft.dateOfBirth = coerceDateOfBirth(value);
        break;
      case 'grade':
        draft.grade = coerceGrade(value);
        break;
      case 'primaryDisability':
        draft.primaryDisability = coerceDisability(value);
        break;
      case 'iepStartDate':
        draft.iepStartDate = coerceDateOfBirth(value);
        break;
      case 'iepAnnualReviewDue':
        draft.iepAnnualReviewDue = coerceDateOfBirth(value);
        break;
      case 'notes':
        draft.notes = String(value).trim();
        break;
      default:
        break;
    }
  }
  return draft;
}

// Validate a draft row. Returns { valid, warnings[], errors[] }.
// A row is `valid` (importable) if it has no errors — warnings still
// let the row through but flag it for the teacher to review.
export function validateDraft(draft) {
  const errors = [];
  const warnings = [];
  if (!draft.firstName) errors.push('Missing first name.');
  if (!draft.lastName) errors.push('Missing last name.');
  if (draft.dateOfBirth === null && (draft.rawDob || '') !== '') {
    // rawDob is only set by the wizard when we've tried and failed
    // to parse a value. Missing DOB (empty string) is fine — that's
    // an optional field.
    warnings.push('Date of birth couldn\'t be parsed — will be left blank.');
  }
  return {
    valid: errors.length === 0,
    warnings,
    errors,
  };
}

// Best-effort duplicate detection against the existing roster.
// Returns an array of `{ existingId, existingName, reason }` matches
// so the UI can show "This looks like Joe Jones (already on your
// roster)" and let the teacher decide skip vs. add-anyway.
export function findLikelyDuplicates(draft, existingRoster) {
  if (!Array.isArray(existingRoster) || existingRoster.length === 0) return [];
  const firstLower = (draft.firstName || '').toLowerCase();
  const lastLower = (draft.lastName || '').toLowerCase();
  if (!firstLower || !lastLower) return [];
  const dob = draft.dateOfBirth || null;

  const matches = [];
  for (const student of existingRoster) {
    const f = (student.firstName || '').toLowerCase();
    const l = (student.lastName || '').toLowerCase();
    if (f !== firstLower || l !== lastLower) continue;

    const existingDob = student.dateOfBirth || null;
    if (dob && existingDob) {
      if (dob === existingDob) {
        matches.push({
          existingId: student.id,
          existingName: `${student.firstName} ${student.lastName}`.trim(),
          reason: 'Same name and date of birth',
        });
      }
      // Different DOB — probably a genuine second student with the
      // same name; don't flag as a dup.
    } else {
      matches.push({
        existingId: student.id,
        existingName: `${student.firstName} ${student.lastName}`.trim(),
        reason: 'Same name (no DOB to distinguish)',
      });
    }
  }
  return matches;
}

// Build a downloadable CSV template with all our canonical headers plus
// two illustrative example rows. Excel will open this without warnings.
export function buildCsvTemplate() {
  const headers = [
    'First Name',
    'Last Name',
    'Date of Birth',
    'Grade',
    'Primary Disability',
    'IEP Start Date',
    'IEP Annual Review Due',
    'Notes',
  ];
  const examples = [
    ['Joe',    'Jones',   '5/12/2015',  '3', 'Autism', '9/1/2025', '9/1/2026', 'Uses AAC device'],
    ['Maria',  'Garcia',  '2015-08-04', 'K', 'SLD',    '',         '',         ''],
  ];
  return Papa.unparse([headers, ...examples]);
}
